import { useEffect, useState } from "react";
import { ShoppingCart, TrendingUp, DollarSign, FileText, CreditCard } from "lucide-react";
import { DataTablePattern } from "../../patterns/DataTablePattern";
import { getAmbassadorPurchases } from "../../../api/admin";

type AmbassadorPurchasesTabProps = {
  ambassadorId: string;
};

export function AmbassadorPurchasesTab({ ambassadorId }: AmbassadorPurchasesTabProps) {
  const [purchases, setPurchases] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAmbassadorPurchases(ambassadorId)
      .then((data) => {
        if (!mounted) return;
        setPurchases(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load purchases:", error);
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ambassadorId]);

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="text-sm text-muted">Chargement...</div>
      </div>
    );
  }

  const purchasesData = purchases?.purchases || {
    quotes_received: [],
    quotes_accepted: [],
    invoices_received: [],
    credits_purchased: 0,
    payment_status: {},
    material_purchases: [],
  };

  const salesData = purchases?.sales || {
    quotes_sent: [],
    quotes_accepted: [],
    quotes_rejected: [],
    invoices_generated: [],
    sales_volume: 0,
    commission_generated: 0,
  };

  return (
    <div className="space-y-6">
      {/* A. Achats (Ambassadeur → XLKey) */}
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingCart size={16} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-text">Achats (Ambassadeur → XLKey)</h3>
        </div>

        {/* Indicateurs */}
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Solde crédits</div>
            <div className="text-2xl font-semibold text-text">
              {purchasesData.credits_purchased?.toLocaleString() || "0"}
            </div>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Volume achats YTD</div>
            <div className="text-2xl font-semibold text-text">
              ${purchasesData.ytd_volume?.toLocaleString() || "0"}
            </div>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Factures impayées</div>
            <div className="text-2xl font-semibold text-danger">
              {purchasesData.unpaid_invoices_count || 0}
            </div>
          </div>
        </div>

        {/* Soumissions reçues */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-text mb-2">Soumissions reçues</h4>
          <DataTablePattern
            title=""
            columns={[
              { key: "quote_number", label: "Numéro", type: "text" },
              { key: "status", label: "Statut", type: "text" },
              { key: "total", label: "Montant", type: "text" },
              { key: "created_at", label: "Date", type: "text" },
            ]}
            rows={purchasesData.quotes_received?.map((row: any) => ({
              id: row.id,
              quote_number: row.quote_number || "—",
              status: row.status || "—",
              total: row.total ? `$${row.total.toLocaleString()}` : "—",
              created_at: row.created_at ? new Date(row.created_at).toLocaleDateString("fr-CA") : "—",
            })) || []}
          />
        </div>

        {/* Factures reçues */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-text mb-2">Factures reçues</h4>
          <DataTablePattern
            title=""
            columns={[
              { key: "invoice_number", label: "Numéro", type: "text" },
              { key: "status", label: "Statut", type: "text" },
              { key: "total", label: "Montant", type: "text" },
              { key: "issue_date", label: "Date", type: "text" },
              { key: "due_date", label: "Échéance", type: "text" },
            ]}
            rows={purchasesData.invoices_received?.map((inv: any) => ({
              id: inv.id,
              invoice_number: inv.invoice_number || "—",
              status: inv.status || "—",
              total: inv.total ? `$${inv.total.toLocaleString()}` : "—",
              issue_date: inv.issue_date ? new Date(inv.issue_date).toLocaleDateString("fr-CA") : "—",
              due_date: inv.due_date ? new Date(inv.due_date).toLocaleDateString("fr-CA") : "—",
            })) || []}
          />
        </div>
      </div>

      {/* B. Ventes (Ambassadeur → Clients) */}
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-text">Ventes (Ambassadeur → Clients)</h3>
        </div>

        {/* Indicateurs */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Taux conversion</div>
            <div className="text-2xl font-semibold text-text">
              {salesData.conversion_rate ? `${salesData.conversion_rate.toFixed(1)}%` : "—"}
            </div>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Volume crédits vendus</div>
            <div className="text-2xl font-semibold text-text">
              {salesData.credits_sold?.toLocaleString() || "0"}
            </div>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Performance mensuelle</div>
            <div className="text-2xl font-semibold text-text">
              ${salesData.monthly_volume?.toLocaleString() || "0"}
            </div>
          </div>
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="text-xs text-muted mb-1">Commission générée</div>
            <div className="text-2xl font-semibold text-success">
              ${salesData.commission_generated?.toLocaleString() || "0"}
            </div>
          </div>
        </div>

        {/* Soumissions envoyées */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-text mb-2">Soumissions envoyées</h4>
          <DataTablePattern
            title=""
            columns={[
              { key: "quote_number", label: "Numéro", type: "text" },
              { key: "client_name", label: "Client", type: "text" },
              { key: "status", label: "Statut", type: "text" },
              { key: "total", label: "Montant", type: "text" },
              { key: "created_at", label: "Date", type: "text" },
            ]}
            rows={salesData.quotes_sent?.map((row: any) => ({
              id: row.id,
              quote_number: row.quote_number || "—",
              client_name: row.client_name || "—",
              status: row.status || "—",
              total: row.total ? `$${row.total.toLocaleString()}` : "—",
              created_at: row.created_at ? new Date(row.created_at).toLocaleDateString("fr-CA") : "—",
            })) || []}
          />
        </div>

        {/* Factures générées */}
        <div>
          <h4 className="text-xs font-semibold text-text mb-2">Factures générées</h4>
          <DataTablePattern
            title=""
            columns={[
              { key: "invoice_number", label: "Numéro", type: "text" },
              { key: "client_name", label: "Client", type: "text" },
              { key: "status", label: "Statut", type: "text" },
              { key: "total", label: "Montant", type: "text" },
              { key: "issue_date", label: "Date", type: "text" },
            ]}
            rows={salesData.invoices_generated?.map((inv: any) => ({
              id: inv.id,
              invoice_number: inv.invoice_number || "—",
              client_name: inv.client_name || "—",
              status: inv.status || "—",
              total: inv.total ? `$${inv.total.toLocaleString()}` : "—",
              issue_date: inv.issue_date ? new Date(inv.issue_date).toLocaleDateString("fr-CA") : "—",
            })) || []}
          />
        </div>
      </div>
    </div>
  );
}
